import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Modal,
  Result,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import {
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { SetPageHeader } from '@actions/userActions';
import { useAppDispatch, useAppSelector } from '@config/reduxStore';
import { McpAuthorization, McpConnectionStatus, McpScope, McpSettingsRequest } from '@models/Mcp';
import {
  ALL_MCP_SCOPES,
  MCP_SCOPES,
  canManageMcp,
  deleteMcpAuthorization,
  getMcpAuthorizations,
  getMcpErrorStatus,
  getMcpServerUrl,
  getMcpSettings,
  getScopeRisk,
  getSensitiveScopeWarning,
  isMcpBackendUnavailable,
  saveMcpSettings,
} from '@services/mcpOAuth';

const { Paragraph, Text, Title } = Typography;

const riskColor = {
  baixo: 'green',
  medio: 'orange',
  alto: 'red',
};

function formatDate(value?: string) {
  if (!value) return 'Nunca';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

function ScopeTags({ scopes }: { scopes: McpScope[] }) {
  return (
    <Space size={[4, 4]} wrap>
      {scopes.map((scope) => (
        <Tag key={scope} color={riskColor[MCP_SCOPES[scope].risk]}>
          {MCP_SCOPES[scope].label}
        </Tag>
      ))}
    </Space>
  );
}

function StatusTag({ status }: { status: McpConnectionStatus }) {
  const color = status === 'active' ? 'green' : status === 'revoked' ? 'default' : 'gold';
  const label = {
    active: 'Ativa',
    inactive: 'Inativa',
    revoked: 'Revogada',
    expired: 'Expirada',
    pending: 'Pendente',
  }[status];
  return <Tag color={color}>{label}</Tag>;
}

function CommandLine({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);

  async function copyCommand() {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Text code style={{ flex: 1, padding: '5px 11px', overflowX: 'auto', whiteSpace: 'nowrap' }}>
        {command}
      </Text>
      <Button
        aria-label={`Copiar comando ${command}`}
        icon={copied ? <CheckOutlined /> : <CopyOutlined />}
        onClick={copyCommand}
      />
    </Space.Compact>
  );
}

export function IntegracoesMcpView() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user.user);
  const [selectedAuthorization, setSelectedAuthorization] = useState<McpAuthorization>(null);
  const [settingsForm] = Form.useForm<McpSettingsRequest>();
  const hasAccess = canManageMcp(user);
  const mcpServerUrl = getMcpServerUrl();

  const settingsQuery = useQuery(['mcp-settings'], getMcpSettings, {
    enabled: hasAccess,
    retry: (failureCount, error) => {
      const status = getMcpErrorStatus(error);
      return !isMcpBackendUnavailable(error) && status !== 401 && status !== 403 && failureCount < 2;
    },
  });

  const authorizationsQuery = useQuery(['mcp-authorizations'], getMcpAuthorizations, {
    enabled: hasAccess,
    retry: (failureCount, error) => {
      const status = getMcpErrorStatus(error);
      return !isMcpBackendUnavailable(error) && status !== 401 && status !== 403 && failureCount < 2;
    },
  });

  useEffect(() => {
    dispatch(
      SetPageHeader({
        title: 'Integrações de IA',
        backbutton: true,
        extras: [],
        subtitle: 'MCP / Agentes de IA',
        menu: 'integracoesia',
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (!settingsQuery.data) return;
    settingsForm.setFieldsValue({
      enabled: settingsQuery.data.enabled,
      allowedScopes: settingsQuery.data.allowedScopes,
    });
  }, [settingsForm, settingsQuery.data]);

  const saveSettingsMutation = useMutation((values: McpSettingsRequest) => saveMcpSettings(values), {
    onSuccess: () => {
      notification.success({ message: 'Configurações MCP salvas.' });
      queryClient.invalidateQueries(['mcp-settings']);
    },
    onError: () => notification.error({ message: 'Não foi possível salvar as configurações MCP.' }),
  });

  const revokeAuthorizationMutation = useMutation((authorization: McpAuthorization) => deleteMcpAuthorization(authorization.id), {
    onSuccess: () => {
      notification.success({ message: 'Autorização MCP revogada.' });
      queryClient.invalidateQueries(['mcp-authorizations']);
    },
    onError: () => notification.error({ message: 'Não foi possível revogar a autorização MCP.' }),
  });

  const authorizations = useMemo(() => authorizationsQuery.data || [], [authorizationsQuery.data]);
  const primaryError = settingsQuery.error || authorizationsQuery.error;
  const isMcpUnavailable = (settingsQuery.isError || authorizationsQuery.isError) && isMcpBackendUnavailable(primaryError);
  const mcpErrorStatus = getMcpErrorStatus(primaryError);
  const isMcpUnauthorized =
    (settingsQuery.isError || authorizationsQuery.isError) && (mcpErrorStatus === 401 || mcpErrorStatus === 403);
  const isMcpLoadError = (settingsQuery.isError || authorizationsQuery.isError) && !isMcpUnavailable && !isMcpUnauthorized;

  if (!hasAccess) {
    return (
      <Result
        status="403"
        title="Área restrita"
        subTitle="Somente administradores autorizados podem gerenciar autorizações MCP e agentes de IA."
      />
    );
  }

  function confirmRevoke(authorization: McpAuthorization) {
    Modal.confirm({
      title: 'Revogar autorização MCP?',
      icon: <DeleteOutlined />,
      content: 'O agente perderá acesso imediatamente aos dados autorizados para este tenant.',
      okText: 'Revogar',
      okButtonProps: { danger: true, loading: revokeAuthorizationMutation.isLoading },
      cancelText: 'Cancelar',
      onOk: () => revokeAuthorizationMutation.mutateAsync(authorization),
    });
  }

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          showIcon
          icon={<SafetyCertificateOutlined />}
          type="warning"
          message="Defina os limites do tenant antes de autorizar agentes"
          description="Cada cliente OAuth só recebe um subconjunto dos escopos permitidos aqui. Dados sensíveis exigem revisão explícita antes de habilitar."
        />

        <Card size="small">
          <Space direction="vertical" size={2}>
            <Title level={4} style={{ margin: 0 }}>
              MCP / Agentes de IA
            </Title>
            <Paragraph type="secondary" style={{ margin: 0 }}>
              Governança do servidor MCP do tenant: escopos permitidos, autorizações ativas e revogação de tokens.
            </Paragraph>
          </Space>
        </Card>

        {isMcpUnavailable && (
          <Alert
            showIcon
            type="info"
            message="Integrações MCP indisponíveis"
            description="A API ainda não expõe os endpoints de administração MCP esperados para esta versão."
          />
        )}

        {isMcpUnauthorized && (
          <Alert
            showIcon
            type="warning"
            message="API MCP recusou a autorização"
            description={`A API retornou HTTP ${mcpErrorStatus}. Confirme se o token atual foi renovado e se o usuário possui role admin/master ou permissão mcp.manage.`}
          />
        )}

        {isMcpLoadError && (
          <Alert
            showIcon
            type="error"
            message="Não foi possível carregar as integrações MCP"
            description={
              mcpErrorStatus
                ? `A API MCP retornou HTTP ${mcpErrorStatus}.`
                : 'A chamada à API MCP falhou antes de receber resposta.'
            }
          />
        )}

        <Card size="small" title="Configuração do tenant">
          <Form
            form={settingsForm}
            layout="vertical"
            initialValues={{ enabled: false, allowedScopes: ['mcp.read.projects'] }}
            onFinish={(values) => saveSettingsMutation.mutate(values)}
          >
            <Form.Item name="enabled" label="Servidor MCP" valuePropName="checked">
              <Switch checkedChildren="Habilitado" unCheckedChildren="Desabilitado" />
            </Form.Item>

            <Form.Item
              name="allowedScopes"
              label="Escopos permitidos"
              rules={[{ required: true, message: 'Selecione ao menos um escopo permitido.' }]}
            >
              <Checkbox.Group style={{ width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {ALL_MCP_SCOPES.map((scope) => (
                    <Checkbox key={scope} value={scope}>
                      <Space direction="vertical" size={0}>
                        <Text strong>{MCP_SCOPES[scope].label}</Text>
                        <Text type="secondary">{MCP_SCOPES[scope].description}</Text>
                      </Space>
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {() => {
                const warning = getSensitiveScopeWarning(settingsForm.getFieldValue('allowedScopes') || []);
                return warning ? (
                  <Alert showIcon type="error" message="Escopos sensíveis habilitados" description={warning} style={{ marginBottom: 16 }} />
                ) : null;
              }}
            </Form.Item>

            <Button type="primary" htmlType="submit" loading={saveSettingsMutation.isLoading}>
              Salvar configurações
            </Button>
          </Form>
        </Card>

        <Card size="small" title="Como conectar">
          <Space direction="vertical" style={{ width: '100%' }}>
            <CommandLine command={`codex mcp add elevargestao --url ${mcpServerUrl}`} />
            <CommandLine command="codex mcp login elevargestao" />
          </Space>
        </Card>

        <Table
          rowKey="id"
          loading={
            (settingsQuery.isLoading || authorizationsQuery.isLoading) &&
            !isMcpUnavailable &&
            !isMcpUnauthorized &&
            !isMcpLoadError
          }
          dataSource={authorizations}
          locale={{ emptyText: <Empty description="Nenhuma autorização MCP ativa" /> }}
          scroll={{ x: 1100 }}
          pagination={{
            position: ['bottomRight'],
            size: 'small',
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50],
          }}
        >
          <Table.Column<McpAuthorization>
            width={96}
            fixed="left"
            render={(_, authorization) => (
              <Space>
                <Button
                  aria-label={`Ver detalhes de ${authorization.clientName}`}
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => setSelectedAuthorization(authorization)}
                />
                <Button
                  aria-label={`Revogar ${authorization.clientName}`}
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={authorization.status === 'revoked'}
                  onClick={() => confirmRevoke(authorization)}
                />
              </Space>
            )}
          />
          <Table.Column<McpAuthorization> title="Agente/app" dataIndex="clientName" />
          <Table.Column<McpAuthorization> title="ClientId" dataIndex="clientId" render={(value) => <Text code>{value}</Text>} />
          <Table.Column<McpAuthorization> title="Escopos" dataIndex="scopes" render={(scopes) => <ScopeTags scopes={scopes} />} />
          <Table.Column<McpAuthorization>
            title="Risco"
            render={(_, authorization) => {
              const risk = getScopeRisk(authorization.scopes);
              return <Tag color={riskColor[risk]}>{risk.toUpperCase()}</Tag>;
            }}
          />
          <Table.Column<McpAuthorization> title="Autorizado por" dataIndex="authorizedBy" render={(value) => value || '-'} />
          <Table.Column<McpAuthorization> title="Criado em" dataIndex="createdAt" render={formatDate} />
          <Table.Column<McpAuthorization> title="Último uso" dataIndex="lastUsedAt" render={formatDate} />
          <Table.Column<McpAuthorization> title="Status" dataIndex="status" render={(status) => <StatusTag status={status} />} />
        </Table>
      </Space>

      <Drawer
        title="Detalhes da autorização MCP"
        open={!!selectedAuthorization}
        onClose={() => setSelectedAuthorization(null)}
        width={560}
      >
        {selectedAuthorization && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Agente/app">{selectedAuthorization.clientName}</Descriptions.Item>
              <Descriptions.Item label="ClientId">
                <Text code>{selectedAuthorization.clientId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Autorizado por">{selectedAuthorization.authorizedBy || 'Usuário autorizado'}</Descriptions.Item>
              <Descriptions.Item label="Criado em">{formatDate(selectedAuthorization.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Último acesso">{formatDate(selectedAuthorization.lastUsedAt)}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <StatusTag status={selectedAuthorization.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Escopos">
                <ScopeTags scopes={selectedAuthorization.scopes} />
              </Descriptions.Item>
              <Descriptions.Item label="Tools/áreas acessíveis">
                {(selectedAuthorization.tools || []).join(', ') || 'Derivadas dos escopos do token'}
              </Descriptions.Item>
            </Descriptions>
            {getSensitiveScopeWarning(selectedAuthorization.scopes) && (
              <Alert
                showIcon
                type="error"
                message="Autorização com dados sensíveis"
                description={getSensitiveScopeWarning(selectedAuthorization.scopes)}
              />
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
}

export default IntegracoesMcpView;
